import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
    OrthodontistDetailInfo,
    OrthodontistPracticeSummary
} from '../../../../services/orthodontistAccounts';
import {
    DEFAULT_DETAIL_SECTION,
    isDetailSection,
    type DetailSectionId
} from './orthodontistDetailSections';
import { OrthodontistDetailNav } from './OrthodontistDetailNav';
import { OrthodontistInfoSection } from './OrthodontistInfoSection';
import { OrthodontistFilesSection } from './OrthodontistFilesSection';
import { OrthodontistFeatureSettingsSection } from './OrthodontistFeatureSettingsSection';
import { OrthodontistPreferencesProtocolsSection } from './OrthodontistPreferencesProtocolsSection';
import { SaveBar } from '../../practice/details/SaveBar';
import { useOrthodontistInfoForm } from './useOrthodontistInfoForm';

// Same `?section=` + `replace` convention as OrthodontistDetail's own tab
// handling - a missing/garbage param falls back to the default pane.
function readSectionFromSearch(search: string): DetailSectionId {
    const raw = new URLSearchParams(search).get('section');
    return raw && isDetailSection(raw) ? raw : DEFAULT_DETAIL_SECTION;
}

interface OrthodontistDetailsTabProps {
    orthodontist: OrthodontistDetailInfo;
    practices: OrthodontistPracticeSummary[];
}

// Form state lives here, not in the Info pane, so unsaved edits and the save
// bar survive switching panes (same as the practice DetailsTab).
export function OrthodontistDetailsTab({ orthodontist, practices }: OrthodontistDetailsTabProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const form = useOrthodontistInfoForm(orthodontist);
    const [activeSection, setActiveSection] = useState<DetailSectionId>(() =>
        readSectionFromSearch(location.search)
    );

    useEffect(() => {
        const next = readSectionFromSearch(location.search);
        setActiveSection((prev) => (next === prev ? prev : next));
    }, [location.search]);

    const handleSectionChange = (section: DetailSectionId) => {
        setActiveSection(section);
        const params = new URLSearchParams(location.search);
        if (section === DEFAULT_DETAIL_SECTION) {
            params.delete('section');
        } else {
            params.set('section', section);
        }
        const search = params.toString();
        navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    };

    return (
        <div className="flex-1 min-h-0 flex items-stretch gap-5 overflow-hidden">
            <OrthodontistDetailNav activeId={activeSection} onSelect={handleSectionChange} />
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-3.5">
                <SaveBar
                    phase={form.phase}
                    dirtyCount={form.dirtyCount}
                    dirtyLabels={form.dirtyLabels}
                    onSave={form.onSave}
                    onDiscard={form.onDiscard}
                />
                {activeSection === 'info' && (
                    <OrthodontistInfoSection
                        orthodontist={orthodontist}
                        practices={practices}
                        values={form.values}
                        fieldError={form.fieldError}
                        disabled={form.isSaving}
                        onFieldChange={form.onFieldChange}
                    />
                )}
                {activeSection === 'files' && <OrthodontistFilesSection />}
                {activeSection === 'beta' && (
                    <OrthodontistFeatureSettingsSection orthodontistId={orthodontist.id} />
                )}
                {activeSection === 'preferences' && (
                    <OrthodontistPreferencesProtocolsSection orthodontistId={orthodontist.id} />
                )}
            </div>
        </div>
    );
}
